import { useMemo, useState } from "react";
import { isAxiosError } from "axios";
import JSZip from "jszip";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker as AntDatePicker, ConfigProvider } from "antd";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  LinearProgress,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Download as DownloadIcon,
  FolderOpen as ViewerIcon,
  InfoOutlined as InfoIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Upload as ForceUploadIcon,
} from "@mui/icons-material";
import { useForceUploadLog } from "@/hooks/use-device-log-files";
import { useLogArchive } from "@/hooks/use-log-archive";
import { api } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import { formatRelativeTime } from "@/lib/timezone";
import { type ViewerFile } from "./multi-file-log-viewer";
import { MultiFileLogKlogg, KLOGG_MAX_FILES } from "./multi-file-log-klogg";
import type { DeviceStatus, LogArchiveObject } from "@/types";

interface MergedLogsBrowserProps {
  deviceId: string;
  deviceStatus: DeviceStatus;
}

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

interface MergedRow {
  key: string;          // dedupe key — `<bucket>:<name>` (full GCS path)
  display: string;      // basename shown to the user
  size: number;
  packageLabel: string; // bucket name
  date: string;         // ISO — GCS Updated (authoritative upload time)
  bucket: string;       // configured archive bucket
  archiveName: string;  // full GCS object key
}

function basename(path: string): string {
  const i = path.lastIndexOf("/");
  return i === -1 ? path : path.slice(i + 1);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatFullDate(iso: string | undefined | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime()) || d.getUTCFullYear() < 2000) return "—";
  // YYYY-MM-DD HH:MM:SS in UTC — matches the GCS console and avoids
  // surprises when comparing with backend logs.
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
}

function isZip(name: string): boolean {
  return name.toLowerCase().endsWith(".zip");
}

/** Convert GCS archive objects into table rows. Dedupes by `<bucket>:<name>`
 *  so the same file scanned by multiple prefix queries collapses to one row.
 */
function buildRows(archive: LogArchiveObject[]): MergedRow[] {
  const map = new Map<string, MergedRow>();
  for (const o of archive) {
    const key = `${o.bucket}:${o.name}`;
    if (map.has(key)) continue;
    map.set(key, {
      key,
      display: basename(o.name),
      size: o.size,
      packageLabel: o.bucket,
      date: o.updated,
      bucket: o.bucket,
      archiveName: o.name,
    });
  }
  return Array.from(map.values()).sort((a, b) => b.date.localeCompare(a.date));
}

export function MergedLogsBrowser({ deviceId, deviceStatus }: MergedLogsBrowserProps) {
  // Default both ends empty so the first open pulls the whole bucket: the
  // request omits from/to and the server returns everything (max-range cap and
  // lower-bound filter are off for testing). Pick a range to narrow; clear to
  // go back to all.
  const [toDayjs, setToDayjs] = useState<Dayjs | null>(null);
  const [fromDayjs, setFromDayjs] = useState<Dayjs | null>(null);
  // Hooks talk to the API in YYYY-MM-DD; pickers own real Dayjs objects.
  const from = fromDayjs ? fromDayjs.format("YYYY-MM-DD") : "";
  const to = toDayjs ? toDayjs.format("YYYY-MM-DD") : "";
  // Stable identity for the RangePicker value. rc-picker re-syncs its
  // in-progress selection from `value` whenever the array *reference* changes,
  // and onChange only commits once BOTH ends are picked. A fresh
  // [fromDayjs, toDayjs] literal every render lets any unrelated parent
  // re-render (device-status WS batch, archive query settling, the isFetching
  // progress bar) wipe a half-finished range — you pick a start, the parent
  // re-renders, and the start is gone before you pick the end.
  const pickerValue = useMemo<[Dayjs | null, Dayjs | null]>(
    () => [fromDayjs, toDayjs],
    [fromDayjs, toDayjs],
  );
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [toast, setToast] = useState<{ severity: "success" | "error" | "info"; msg: string } | null>(null);

  const archiveQuery = useLogArchive(deviceId, from, to);
  const [isDownloading, setIsDownloading] = useState(false);
  const forceUpload = useForceUploadLog(deviceId);

  const isLoading = archiveQuery.isLoading;
  const isFetching = archiveQuery.isFetching;

  const archiveError = (() => {
    if (!archiveQuery.error) return null;
    if (isAxiosError(archiveQuery.error) && archiveQuery.error.response?.status === 404) {
      return "Log archive is not configured on this server.";
    }
    if (isAxiosError(archiveQuery.error)) {
      return (archiveQuery.error.response?.data as { message?: string } | undefined)?.message
        ?? archiveQuery.error.message;
    }
    return "Failed to load archive.";
  })();

  const maxRangeDays = archiveQuery.data?.max_range_days ?? 0;
  const rangeTooWide = (() => {
    if (!maxRangeDays) return false;
    const ms = new Date(to).getTime() - new Date(from).getTime();
    return ms / (1000 * 60 * 60 * 24) > maxRangeDays;
  })();

  const allRows = useMemo(
    () => buildRows(archiveQuery.data?.objects ?? []),
    [archiveQuery.data],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allRows.filter((r) => {
      if (q && !(r.display.toLowerCase().includes(q) || r.packageLabel.toLowerCase().includes(q))) return false;
      return true;
    });
  }, [allRows, search]);

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const selectedRows = useMemo(
    () => allRows.filter((r) => selected.has(r.key)),
    [allRows, selected],
  );

  // Every selected row resolves to an archive download URL. The viewer
  // decides zip-vs-text from `isZip` (filename extension).
  const viewerEligible = useMemo<ViewerFile[]>(() => {
    return selectedRows.map((r) => {
      const params = new URLSearchParams({ bucket: r.bucket, name: r.archiveName });
      return {
        displayName: r.display,
        url: `/devices/${deviceId}/log-archive/download?${params.toString()}`,
        isZip: isZip(r.display),
      };
    });
  }, [selectedRows, deviceId]);

  const toggleRow = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllOnPage = () => {
    const pageKeys = paged.map((r) => r.key);
    const allSelected = pageKeys.every((k) => selected.has(k));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) pageKeys.forEach((k) => next.delete(k));
      else pageKeys.forEach((k) => next.add(k));
      return next;
    });
  };

  const refetchAll = () => {
    archiveQuery.refetch();
  };

  const downloadArchive = async (bucket: string, name: string) => {
    // Server proxy-streams the GCS object — no signed URL needed.
    const params = new URLSearchParams({ bucket, name });
    const slash = name.lastIndexOf("/");
    const fileName = slash === -1 ? name : name.slice(slash + 1);
    setIsDownloading(true);
    try {
      await downloadBlob(`/devices/${deviceId}/log-archive/download?${params.toString()}`, fileName);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRowDownload = (row: MergedRow) => {
    void downloadArchive(row.bucket, row.archiveName);
  };

  // Bundle every selected archive into a single .zip the browser saves once,
  // instead of firing N sequential downloads. A lone selection skips the
  // wrapper — no point nesting one file in a zip.
  const handleBulkDownload = async () => {
    if (selectedRows.length === 0) return;
    if (selectedRows.length === 1) {
      const r = selectedRows[0];
      try { await downloadArchive(r.bucket, r.archiveName); }
      catch (e) {   console.error("archive download failed", e); }
      return;
    }

    setIsDownloading(true);
    const zip = new JSZip();
    const used = new Set<string>();
    let failed = 0;
    try {
      for (const r of selectedRows) {
        const params = new URLSearchParams({ bucket: r.bucket, name: r.archiveName });
        try {
          const res = await api.get(
            `/devices/${deviceId}/log-archive/download?${params.toString()}`,
            { responseType: "arraybuffer" },
          );
          // Disambiguate same-named files coming from different buckets.
          let name = r.display;
          if (used.has(name)) {
            const dot = name.lastIndexOf(".");
            const stem = dot === -1 ? name : name.slice(0, dot);
            const ext = dot === -1 ? "" : name.slice(dot);
            let i = 2;
            while (used.has(`${stem} (${i})${ext}`)) i++;
            name = `${stem} (${i})${ext}`;
          }
          used.add(name);
          // Already-compressed archives are stored as-is so we don't burn
          // CPU re-deflating incompressible bytes.
          zip.file(name, res.data as ArrayBuffer, isZip(name) ? { compression: "STORE" } : undefined);
        } catch (e) {
          failed++;
            console.error("archive fetch failed", e);
        }
      }

      if (used.size === 0) {
        setToast({ severity: "error", msg: "Failed to download any of the selected files." });
        return;
      }

      const blob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${deviceId}-logs-${dayjs().format("YYYYMMDD-HHmmss")}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);

      if (failed > 0) {
        setToast({ severity: "info", msg: `Zipped ${used.size} file(s); ${failed} could not be downloaded.` });
      }
    } catch (e) {
        console.error("zip bundle failed", e);
      setToast({ severity: "error", msg: "Failed to build the zip archive." });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleForceUpload = () => {
    forceUpload.mutate(undefined, {
      onSuccess: () => setToast({ severity: "success", msg: "Force-upload requested. The device will upload its current logs shortly." }),
      onError: (err) => {
        if (isAxiosError(err) && err.response?.status === 429) {
          setToast({ severity: "error", msg: "Cooldown active. Wait before triggering another force upload." });
        } else {
          setToast({ severity: "error", msg: "Failed to request force upload." });
        }
      },
    });
  };

  const isOnline = deviceStatus === "online";
  const pageAllSelected = paged.length > 0 && paged.every((r) => selected.has(r.key));
  const pageSomeSelected = paged.some((r) => selected.has(r.key));

  return (
    <ConfigProvider>
    <Box sx={{ p: 2, position: "relative" }}>
      {/* Top-edge progress bar — visible on first load AND every Refresh / F5,
          so the user always has feedback that an API call is in flight. */}
      {isFetching && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            bgcolor: "rgba(255,103,32,0.1)",
            "& .MuiLinearProgress-bar": { bgcolor: "#FF6720" },
          }}
        />
      )}
      {/* Sources & retention note — explains why some old files may still
          appear after a delete, and the hard ceiling on the date range. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 0.75,
          mb: 1.5,
          px: 1.25,
          py: 0.75,
          borderRadius: 1,
          bgcolor: "rgba(255,103,32,0.06)",
          border: "1px solid rgba(255,103,32,0.15)",
        }}
      >
        <InfoIcon sx={{ fontSize: 16, color: "#FF6720", mt: "2px", flexShrink: 0 }} />
        <Typography variant="caption" sx={{ color: "text.secondary", lineHeight: 1.5 }}>
          Logs are pulled from two GCS buckets: <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 600 }}>machine-log</Box> (legacy) and{" "}
          <Box component="span" sx={{ fontFamily: "monospace", fontWeight: 600 }}>ats1-log-us</Box> (current). Max window:{" "}
          {maxRangeDays || 60} days per query. Deleted objects remain ~7 days in GCS soft-delete before being purged.
        </Typography>
      </Box>
      {/* Filter row — filters on the left, device-level actions on the right. */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", md: "center" }}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flexWrap: "wrap", gap: 1.5 }}>
          <Tooltip
            title="Pick a date range to narrow results. Leave empty (or clear) to load everything in the bucket."
            placement="top"
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
              <AntDatePicker.RangePicker
                value={pickerValue}
                onChange={(dates) => {
                  const [start, end] = dates ?? [null, null];
                  setFromDayjs(start);
                  setToDayjs(end);
                  setPage(0);
                }}
                format="YYYY-MM-DD"
                allowClear
                // Only future dates are disabled (no logs from the future). The
                // max-range cap is off for testing, so any past span is allowed.
                disabledDate={(current) => !!current && current.isAfter(dayjs(), "day")}
              />
              <Typography variant="caption" sx={{ color: "text.disabled", fontSize: 11, lineHeight: 1.1 }}>
                Empty = all logs in the bucket
              </Typography>
            </Box>
          </Tooltip>
          <TextField
            placeholder="Filter filename"
            size="small"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
            sx={{ minWidth: 220 }}
          />
        </Stack>

        <Box sx={{ flex: 1 }} />

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Re-fetch both tracked log files and the GCS archive scan for the current date range.">
            <span>
              <Button
                variant="text"
                size="small"
                startIcon={
                  isFetching
                    ? <CircularProgress size={16} sx={{ color: "#FF6720" }} />
                    : <RefreshIcon />
                }
                disabled={isFetching}
                onClick={refetchAll}
                sx={{ color: "#FF6720", minWidth: 110 }}
              >
                {isFetching ? "Refreshing…" : "Refresh"}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={isOnline
              ? "Ask the device to package its current logs and upload them now. After it finishes, click Refresh to see the new files."
              : "Device must be online to accept a force-upload request"}
          >
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={forceUpload.isPending ? <CircularProgress size={14} sx={{ color: "white" }} /> : <ForceUploadIcon />}
                disabled={!isOnline || forceUpload.isPending}
                onClick={handleForceUpload}
                sx={{ bgcolor: "#FF6720", "&:hover": { bgcolor: "#F5490F" } }}
              >
                Force Upload
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Contextual bulk-action bar — only visible when rows are selected. */}
      {selectedRows.length > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 1.5,
            px: 2,
            py: 1,
            borderRadius: 1,
            bgcolor: "rgba(255,103,32,0.06)",
            border: "1px solid rgba(255,103,32,0.2)",
          }}
        >
          <Typography variant="body2" fontWeight={500} sx={{ color: "#FF6720" }}>
            {selectedRows.length} selected
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Tooltip
            title={
              viewerEligible.length > KLOGG_MAX_FILES
                ? `Too many files selected (${viewerEligible.length}). The viewer accepts at most ${KLOGG_MAX_FILES} files — deselect some to continue.`
                : "Open selected files in the in-browser viewer. `.zip` archives are unpacked; plain-text logs (e.g. *_aplog.1) are decoded directly."
            }
          >
            <span>
              <Button
                size="small"
                startIcon={<ViewerIcon />}
                disabled={viewerEligible.length === 0 || viewerEligible.length > KLOGG_MAX_FILES}
                onClick={() => setViewerOpen(true)}
              >
                Open in Viewer
                {viewerEligible.length > KLOGG_MAX_FILES && ` (max ${KLOGG_MAX_FILES})`}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={selectedRows.length === 1
            ? "Download the selected file."
            : "Bundle all selected files into a single .zip and download it once."}>
            <span>
              <Button
                size="small"
                variant="outlined"
                startIcon={isDownloading ? <CircularProgress size={14} /> : <DownloadIcon />}
                disabled={isDownloading}
                onClick={handleBulkDownload}
              >
                {selectedRows.length === 1 ? "Download" : `Download ${selectedRows.length} as zip`}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Clear the current selection">
            <Button
              size="small"
              onClick={() => setSelected(new Set())}
              sx={{ color: "text.secondary" }}
            >
              Clear
            </Button>
          </Tooltip>
        </Box>
      )}

      {rangeTooWide && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Selected range is wider than {maxRangeDays} days. Wider queries are allowed,
          but GCS typically keeps only ~{maxRangeDays} days of logs — older dates may return nothing.
        </Alert>
      )}
{archiveError && (
        <Alert severity="error" sx={{ mb: 2 }}>Archive: {archiveError}</Alert>
      )}

      {isLoading && allRows.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : filtered.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          No log files for this machine matching the current filter.
        </Typography>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={pageSomeSelected && !pageAllSelected}
                      checked={pageAllSelected}
                      onChange={toggleAllOnPage}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Upload time reported by GCS, shown in UTC (matches the GCS console).">
                      <span>Date <Box component="span" sx={{ color: "text.disabled", fontSize: 11, fontWeight: 400, ml: 0.5 }}>UTC</Box></span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>File</TableCell>
                  <TableCell align="right">Size</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.map((row) => {
                  // Split <machineId>_<rest>.zip → faint prefix + emphasized suffix
                  // so scanning down a column of repetitive filenames is easier.
                  const u = row.display.indexOf("_");
                  const prefix = u === -1 ? "" : row.display.slice(0, u + 1);
                  const suffix = u === -1 ? row.display : row.display.slice(u + 1);
                  return (
                    <TableRow key={row.key} hover selected={selected.has(row.key)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selected.has(row.key)}
                          onChange={() => toggleRow(row.key)}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontVariantNumeric: "tabular-nums" }}
                        >
                          {formatFullDate(row.date)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatRelativeTime(row.date)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 420 }}>
                        <Tooltip
                          title={row.archiveName ? `gs://${row.bucket}/${row.archiveName}` : ""}
                          placement="top-start"
                          disableHoverListener={!row.archiveName}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", wordBreak: "break-all", cursor: "default", display: "inline-block" }}
                          >
                            <Box component="span" sx={{ color: "text.disabled" }}>{prefix}</Box>
                            <Box component="span" sx={{ color: "text.primary" }}>{suffix}</Box>
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {formatBytes(row.size)}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Download (streams from GCS through the server)">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleRowDownload(row)}
                              disabled={isDownloading}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          />
        </>
      )}

      <MultiFileLogKlogg
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        files={viewerEligible}
        deviceId={deviceId}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {toast ? <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
    </ConfigProvider>
  );
}

export default MergedLogsBrowser;
